import React from "react";
import { ArrowDown, Phone } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { heroImage } from "../data/categories";
import { LogoMark } from "./LogoMark";
import { ItalyFlag } from "./ItalyFlag";

export const Hero = () => {
  const { t } = useI18n();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden grain"
    >
      {/* Background image */}
      <img
        src={heroImage}
        alt="Industrial workshop"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Overlay — refined dark fade */}
      <div className="absolute inset-0 bg-[#1a3a2a]/85" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0d2419] via-[#1a3a2a]/70 to-[#1a3a2a]/40" />

      {/* Side meta — vertical address */}
      <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 flex-col items-center gap-4 z-10">
        <span className="rotate-[-90deg] origin-center text-overline whitespace-nowrap">
          {t.hero.location}
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 pb-container w-full pb-20 md:pb-28 pt-32">
        <div className="max-w-5xl">
          {/* Company logo + overline */}
          <div className="flex items-center gap-6 mb-10">
            <div
              data-testid="hero-logo-slot"
              aria-label={t.hero.logoLabel}
              className="hidden sm:flex items-center justify-center"
            >
              <LogoMark size={96} />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                <span className="block w-10 h-px bg-[#c9a84c]" />
                <span className="text-overline">{t.hero.overline}</span>
                <ItalyFlag width={18} className="ml-1" />
              </div>
              <span className="mt-2 text-xs uppercase tracking-[0.3em] text-white/50 font-medium">
                {t.hero.since}
              </span>
            </div>
          </div>

          {/* Refined wordmark — lighter weight, elegant */}
          <h1 className="font-heading text-white leading-[0.95] tracking-tight">
            <span
              data-testid="hero-title-1"
              className="block font-light text-4xl sm:text-6xl md:text-7xl lg:text-8xl"
            >
              {t.hero.title1}
            </span>
            <span
              data-testid="hero-title-2"
              className="block font-bold text-5xl sm:text-7xl md:text-8xl lg:text-[7.5rem] text-[#c9a84c] -mt-1 md:-mt-2"
            >
              {t.hero.title2}
            </span>
          </h1>

          {/* Tagline — refined serif-feel via lighter weight & wider tracking */}
          <p className="mt-10 font-heading font-light text-xl md:text-3xl text-white/85 max-w-3xl tracking-tight italic">
            {t.hero.tagline}
          </p>
          <p
            data-testid="hero-sub"
            className="mt-5 max-w-2xl text-base md:text-lg text-white/65 leading-relaxed font-body"
          >
            {t.hero.sub}
          </p>

          {/* CTAs */}
          <div className="mt-12 flex flex-wrap gap-4">
            <button
              data-testid="hero-cta-catalog"
              onClick={() => scrollTo("catalogo")}
              className="inline-flex items-center gap-3 px-7 py-4 bg-[#c9a84c] hover:bg-[#b39440] text-[#1a3a2a] font-semibold text-xs tracking-[0.25em] uppercase transition-colors group"
            >
              {t.hero.cta}
              <ArrowDown
                className="w-4 h-4 group-hover:translate-y-1 transition-transform"
                strokeWidth={2}
              />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollTo("contatti")}
              className="inline-flex items-center gap-3 px-7 py-4 border border-white/40 text-white hover:bg-white hover:text-[#1a3a2a] font-semibold text-xs tracking-[0.25em] uppercase transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              {t.hero.ctaSecondary}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom marquee — refined */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/15 bg-[#0d2419]/60 backdrop-blur-sm overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap py-4">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center shrink-0">
              {[
                "EST. 1956",
                "MADE IN ITALY",
                "ARTIGIANI DAL 1956",
                "QUALITÀ • PRECISIONE",
                "CHIODI INDUSTRIALI",
              ].map((txt, i) => (
                <span
                  key={`${k}-${i}`}
                  className="flex items-center gap-8 px-8 font-heading text-xs tracking-[0.3em] text-white/60"
                >
                  {txt}
                  <span className="w-1.5 h-1.5 bg-[#c9a84c]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
